﻿import type { FragmentMessage } from '@anddone/ajs-util';
import { createLogger } from '@anddone/ajs-logger';
import type { StateManagement } from '../StateManagement';
import { FRAGMENT_CONFIG } from '@anddone/ajs-util';
import type { OrchestratorCommandManager } from './OrchestratorCommandManager';

const logger = createLogger('SDK', 'OrchestratorMessageBus');

/**
 * Manages window postMessage subscription and message distrubution.
 */
export class OrchestratorMessageBus {
  #messageHandler: ((event: MessageEvent) => void) | null = null;
  readonly #messageListeners = new Set<(message: FragmentMessage) => void>();

  public constructor(
    private readonly dataState: StateManagement,
    private readonly commandManager: OrchestratorCommandManager,
  ) {}

  /**
   * Initialize the postMessage listener.
   *
   * @returns void
   */
  public initializeMessageListener(): void {
    const messageHandler = (event: MessageEvent) => {
      // Validate origin if enabled
      const allowedOrigin = this.dataState.config.allowedFragmentOrigin;
      if (event.origin !== allowedOrigin) {
        logger.warn('message', `Rejected message from unauthorized origin: ${event.origin}`, {
          origin: event.origin,
          allowedFragmentOrigin: allowedOrigin,
        });
        return;
      }

      const incomingMessage = event.data as FragmentMessage;
      const message = this.sanitizeInboundMessage(incomingMessage);

      logger.debug(
        'message',
        `Received message type="${message.type}" from "${message.fragmentName ?? 'unknown'}"`,
        {
          origin: event.origin,
          type: message.type,
          fragmentName: message.fragmentName,
          id: message.fragmentId,
          data: message.data,
        },
      );

      this.#messageListeners.forEach(listener => {
        try {
          listener(message);
        } catch (error) {
          logger.error('message', 'Message listener failed', {
            type: message.type,
            fragmentName: message.fragmentName,
            id: message.fragmentId,
            error,
          });
        }
      });
    };

    this.#messageHandler = messageHandler;
    window.addEventListener('message', messageHandler);

    logger.debug('message', 'Window message listener initialized', {
      allowedFragmentOrigin: this.dataState.config.allowedFragmentOrigin,
    });
  }

  /**
   * Remove inbound values from fragments that are not allowed to expose values
   * to the consuming application.
   *
   * This protects the SDK receiving side in case a fragment is misconfigured,
   * compromised, or accidentally sends values that should have been stripped.
   */
  private sanitizeInboundMessage(message: FragmentMessage): FragmentMessage {
    const config = FRAGMENT_CONFIG.get(message.fragmentName);

    const canReceiveValues = !!config && !config.hasSensitiveData && config.canSubmit;

    if (canReceiveValues || !message.data || !('values' in message.data)) {
      return message;
    }

    const { values, ...sanitizedData } = message.data;

    logger.warn(
      'message-values-stripped',
      'Removed unauthorized values from inbound fragment message.',
      {
        fragmentName: message.fragmentName,
        messageType: message.type,
        hasSensitiveData: config?.hasSensitiveData,
        canSubmit: config?.canSubmit,
        valuesRemoved: values?.size,
      },
    );

    return {
      ...message,
      data: sanitizedData,
    } as FragmentMessage;
  }

  /**
   * Register a callback for receiving messages from fragments.
   *
   * @param callback - Function to call when a message is received
   * @returns void
   */
  public onMessage(callback: (message: FragmentMessage) => void): void {
    this.#messageListeners.add(callback);

    logger.debug('message', 'Message listener registered', {
      listenerCount: this.#messageListeners.size,
    });
  }

  /**
   * Remove a message callback.
   *
   * @param callback - Function to remove from listeners
   * @returns void
   */
  public offMessage(callback: (message: FragmentMessage) => void): void {
    this.#messageListeners.delete(callback);

    logger.debug('message', 'Message listener removed', {
      listenerCount: this.#messageListeners.size,
    });
  }

  /**
   * Number of registered message listeners.
   *
   * @returns number
   */
  public listenerCount(): number {
    return this.#messageListeners.size;
  }

  /**
   * Whether the window postMessage listener is currently attached.
   *
   * @returns boolean
   */
  public hasMessageHandler(): boolean {
    return this.#messageHandler !== null;
  }

  /**
   * Remove all registered message listeners.
   *
   * @returns void
   */
  public clear(): void {
    this.#messageListeners.clear();
  }

  /**
   * Remove the window postMessage listener.
   *
   * @returns void
   */
  public removeMessageListener(): void {
    const messageHandler = this.#messageHandler;

    if (!messageHandler) {
      return;
    }

    window.removeEventListener('message', messageHandler);
    this.#messageHandler = null;

    logger.debug('message', 'Window message listener removed');
  }
}
